class ReleaseCountdown extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['timer']
  }

  static get requiredAttributes () {
    return ['release-time']
  }

  connectedCallback () {
    super.connectedCallback()

    const releaseAt = this.getAttribute('release-time')
    this.releaseDate = this.parseReleaseDate(releaseAt)

    if (!this.releaseDate) return

    this.timerElement = this.els.timer.element
    this.hasBuiltDom = false

    this.startCountdown()
  }

  setupListeners () {
    super.setupListeners()
  }

  disconnectedCallback () {
    this.stopCountdown()
  }

  startCountdown () {
    this.stopCountdown()
    this.updateCountdown()

    this.countdownIntervalId = window.setInterval(
      () => this.updateCountdown(),
      1000,
    )
  }

  stopCountdown () {
    if (!this.countdownIntervalId) return
    window.clearInterval(this.countdownIntervalId)
    this.countdownIntervalId = null
  }

  updateCountdown () {
    const millisecondsRemaining = this.releaseDate.getTime() - Date.now()

    if (millisecondsRemaining <= 0) {
      this.stopCountdown()
      this.setAttribute('data-refreshing', 'true')
      this.refreshProductInfoSection()
      return
    }

    this.renderTimeRemaining(millisecondsRemaining)
  }

  renderTimeRemaining (millisecondsRemaining) {
    const totalSeconds = Math.floor(millisecondsRemaining / 1000)

    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const nextValues = { days, hours, minutes, seconds }

    if (!this.hasBuiltDom) {
      this.buildInitialDom(nextValues)
      this.hasBuiltDom = true
      return
    }

    this.updateTickerValue('days', nextValues.days)
    this.updateTickerValue('hours', nextValues.hours)
    this.updateTickerValue('minutes', nextValues.minutes)
    this.updateTickerValue('seconds', nextValues.seconds)
  }

  buildInitialDom ({ days, hours, minutes, seconds }) {
    this.parts = {}
    this.timerElement.textContent = ''

    const fragment = document.createDocumentFragment()

    const appendSeparator = () => {
      const sep = document.createElement('span')
      sep.className = 'prd-Countdown_Separator'
      sep.textContent = ':'
      fragment.appendChild(sep)
    }

    const appendPart = (key, value, unit) => {
      if (fragment.childNodes.length > 0) appendSeparator()

      const part = document.createElement('span')
      part.className = 'prd-Countdown_Part'
      part.setAttribute('data-part', key)

      const valueViewport = document.createElement('span')
      valueViewport.className = 'prd-Countdown_Value'

      const track = document.createElement('span')
      track.className = 'prd-Countdown_ValueTrack'
      track.setAttribute('data-rolling', 'false')

      const current = document.createElement('span')
      current.className = 'prd-Countdown_ValueRow'
      current.setAttribute('data-row', 'current')
      current.textContent = this.format(value)

      const next = document.createElement('span')
      next.className = 'prd-Countdown_ValueRow'
      next.setAttribute('data-row', 'next')
      next.textContent = this.format(value)

      track.appendChild(current)
      track.appendChild(next)
      valueViewport.appendChild(track)

      const unitEl = document.createElement('span')
      unitEl.className = 'prd-Countdown_Unit'
      unitEl.textContent = unit

      part.appendChild(valueViewport)
      part.appendChild(unitEl)

      fragment.appendChild(part)

      this.parts[key] = {
        part,
        track,
        current,
        next,
        currentValue: value,
      }
    }

    appendPart('days', days, 'd')
    appendPart('hours', hours, 'h')
    appendPart('minutes', minutes, 'm')
    appendPart('seconds', seconds, 's')

    this.timerElement.appendChild(fragment)

    Object.values(this.parts).forEach((part) => {
      part.track.setAttribute('data-rolling', 'false')
    })
  }

  updateTickerValue (key, newValue) {
    const part = this.parts[key]
    if (!part) return
    if (newValue === part.currentValue) return

    if (part.track.getAttribute('data-rolling') === 'true') {
      part.current.textContent = this.format(newValue)
      part.next.textContent = this.format(newValue)
      part.track.setAttribute('data-rolling', 'false')
      part.currentValue = newValue
      return
    }

    const formatted = this.format(newValue)

    part.next.textContent = formatted

    part.track.setAttribute('data-rolling', 'false')
    requestAnimationFrame(() => {
      part.track.setAttribute('data-rolling', 'true')
    })

    const onAnimationEnd = () => {
      part.current.textContent = formatted
      part.track.setAttribute('data-rolling', 'false')
      part.currentValue = newValue
      part.track.removeEventListener('animationend', onAnimationEnd)
    }

    part.track.addEventListener('animationend', onAnimationEnd)
  }

  format (value) {
    return String(value).padStart(2, '0')
  }

  parseReleaseDate (raw) {
    if (!raw) return null

    const normalized = raw.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
    const date = new Date(normalized)

    if (Number.isNaN(date.getTime())) return null
    return date
  }

  async refreshProductInfoSection () {
    const currentProductInfo = this.closest('product-info')
    if (!currentProductInfo) return window.location.reload()

    const sectionId =
      currentProductInfo.dataset.originalSection ||
      currentProductInfo.dataset.section

    const sectionsUrl =
      currentProductInfo.dataset.url || window.BAO.sectionRenderer.sectionsUrl

    if (!sectionId) return window.location.reload()

    try {
      const cacheBustedUrl = `${sectionsUrl}${sectionsUrl.includes('?') ? '&' : '?'}_release_refresh=${Date.now()}`

      const htmlString = await window.BAO.sectionRenderer.fetchSingle(
        sectionId,
        cacheBustedUrl,
      )

      const doc = new DOMParser().parseFromString(htmlString, 'text/html')
      const newProductInfo = doc.querySelector('product-info')

      if (!newProductInfo) return window.location.reload()

      window.BAO.Utils.gracefullyReplaceNode(
        currentProductInfo,
        newProductInfo,
        currentProductInfo.preReplaceProductOptionsCallbacks || [],
        currentProductInfo.postReplacePageCallbacks || [],
      )
    } catch (e) {
      console.error(e)
      window.location.reload()
    }
  }
}

if (!customElements.get('release-countdown')) {
  customElements.define('release-countdown', ReleaseCountdown)
}
